import { ILanguages } from '@llws/lift-entity-interfaces';
import app from '@/main';
import { IPanelTemplateWithFields, IPanelWithFields, TPanelField } from '../interfaces/custom-content-store';
import { getFieldsFromTemplate } from './get-fields-from-template';
import { translationPanelModel } from './translation-models';

export const getFields = (template: IPanelTemplateWithFields) => {
  const languages: ILanguages[] = app()?.$rsGlobals.$currentClient?.languages || [];

  const fields = getFieldsFromTemplate(template.fields as TPanelField[]);

  const fieldsWithLanguages: Record<string, TPanelField[]> = {
    en: fields,
  };

  return languages.reduce((acc, curr) => {
    acc[curr.languageCode!] = fields;
    return fieldsWithLanguages;
  }, fieldsWithLanguages);
};

export const getExistingFields = (panel: IPanelWithFields, template: IPanelTemplateWithFields) => {
  const languages: string[] = ['en', ...Array.from((app()?.$rsGlobals.$currentClient?.languages || []), (l) => l.languageCode!) ];

  // When an existent panel has new created fields coming from the Template Panel
  // We must assign their default values and make sure to have the right field value/values
  const fields = template.fields.map((f) => {
    const fieldValues = [] as string[];
    const field = {
      name: f.name,
      subtype: f.subtype,
      value: '',
      values: [],
    } as TPanelField;
    if ((f.subtype === 'checkbox-group' || f.subtype === 'feature-buildings')
    && Array.isArray(f.values)) {
      f.values.forEach((v) => {
        if (v.selected && v.value) {
          fieldValues.push(v.value);
        }
      });

      field.values = fieldValues;
      delete field?.value;
    } else {
      delete field?.values;
      if (f.subtype === 'select-options' && Array.isArray(f.values)) {
        const selected = f.values?.find((v) => {
          if (v && v.selected) return v;
          return null;
        });
        if (selected) {
          field.value = selected.value;
        }
      }
    }
    return field;
  });

  return languages.reduce((acc, cur) => {
    acc[cur] = !acc[cur]
      ? fields
      : fields.map((f) => acc[cur].find((fp) => fp.name === f.name) || f);
    return acc;
  }, panel.fields);
};

export const getPanelsByTemplateGroup = (
  groupKey: string | null = null,
  panels: IPanelWithFields[],
  templates: IPanelTemplateWithFields[],
): IPanelWithFields[] => {
  if (groupKey) {
    return panels
      .filter((p) => templates.some((pt) => pt.key === p.panelTemplateKey && pt.templateGroupKey === groupKey))
      .map((p) => ({ ...p, fields: getExistingFields(p, templates.find((pt) => pt.key === p.panelTemplateKey)!) }))
      .sort((a, b) => (a.order > b.order ? 1 : -1));
  }

  return templates
    .filter((pt) => !pt.templateGroupKey && pt.panelType === 'single-panel')
    .map((pt, idx) => {
      const panel = panels.find((p) => p.panelTemplateKey === pt.key);
      if (panel) panel.name = pt.name;
      return panel
        ? { ...panel, fields: getExistingFields(panel, pt) }
        : {
          id: null,
          clientId: pt.clientId,
          siteId: pt.siteId,
          fields: getFields(pt),
          panelTemplateKey: pt.key,
          name: pt.name,
          order: idx + 1,
          model: pt.model,
          modelId: 0,
          status: 'enabled',
          created: new Date(),
          createdUserId: Number(app()?.$rsGlobals.$currentUser.userId),
        } as IPanelWithFields;
    });
};

/**
 * Returns an object with a key of `value` or `values` depending on the panel field subtype.
 * @param field The `TPanelField` to determine `value` key for.
 * @param fieldsWithValues The array of panel fields from either a template or panel to provide value.
 * @returns `TPanelField`
 */
export const getPanelValueKeyByType = (field: TPanelField, fieldsWithValues: Record<string, string | number | Object | null>[]): TPanelField => {
  if (['checkbox-group', 'featured-buildings'].includes(field.subtype as string)) {
    return {
      ...field,
      values: fieldsWithValues.find((tplField) => tplField.name === field.name)?.values as string[] | undefined,
    };
  }

  return {
    ...field,
    value: (fieldsWithValues.find((tplField) => tplField.name === field.name)?.value ?? '') as string,
  };
};

/**
 * Returns a panel's fields that are not translatable.
 * @param panel
 * @returns `TPanelField[]`
 */
export const getNonTranslatedEnglishFields = (panel: IPanelWithFields) => panel.fields.en
  .filter((field) => (!translationPanelModel.keys.some((key) => key.key === field.subtype)));

/**
 * Returns a panel's fields that are translatable.
 * @param panel
 * @returns `TPanelField[]`
 */
export const getTranslatablePanelFields = (panel: IPanelWithFields, languageCode: string) => (panel.fields[languageCode] ?? [])
  .filter((field) => translationPanelModel.keys.some((key) => key.key === field.subtype));
